import { ESystemType } from '@core/api';
import { IContactPerson, ILeagalPerson, ILicenseInfo } from '../src/components';
import { EBindingType } from '../src/components/app-account/binding-card-form/type';
import { IChannelConfigList } from './air-splitting/products/paymentChannels';
import { yibaoAccountStatusLabel, yibaoAccountStatusStyle } from './yibao';
import { lianCardStatusLabel, lianCardStatusStyle } from './lianlian';
import { EGroupOperateAuthStatus } from './app-account/group';

export interface IUpperSetupAgent {
  /** 代理商id */
  id?: number;
  /** 代理商编号 */
  code?: string;
  /** 父级代理商id */
  parentId?: number;
  /** 代理商名称 */
  agentName?: string;
  /** 代理商类型 */
  agentType?: EAgentType;
  /** 代理商等级 */
  levelType?: EAgentLevel;
  /** 是否启用 */
  enable?: boolean;
  /** 联系人姓名 */
  contactName?: string;
  /** 联系人电话 */
  contactPhone?: string;
  /** 统一社会信用代码 */
  creditCode?: string;
}

/** 代理商级别 */
export enum EAgentLevel {
  /** 特级代理商 */
  Top = 0,
  /** 一级代理商 */
  First = 1,
  /** 二级代理商 */
  Second = 2,
}

export const agentLevelLabel = {
  [EAgentLevel.Top]: '特级',
  [EAgentLevel.First]: '一级',
  [EAgentLevel.Second]: '二级',
};

/** 代理商类型 */
export enum EAgentType {
  /** 个人代理 */
  Personal = 1,
  /** 企业代理 */
  Company = 0,
}

export const agentTypeLabel = {
  [EAgentType.Personal]: '个人代理',
  [EAgentType.Company]: '企业代理',
};

/** 代理商企业类型 */
export enum ECompanyAgentType {
  /** 个人代理 */
  Personal = 'PROPRIETOR',
  /** 企业代理 */
  Company = 'ENTERPRISE',
}

export const companyAgentTypeLabel = {
  [ECompanyAgentType.Personal]: '个体工商户',
  [ECompanyAgentType.Company]: '企业',
};

export enum EAccountType {
  /** 账簿 */
  ACCOUNT_BOOK = 'ACCOUNT_BOOK',
  /** 个人支付账户 */
  PERSONAL_PAYMENT = 'PERSONAL_PAYMENT_ACCOUNT',
  /** 企业支付账户 */
  ENTERPRISE_PAYMENT = 'ENTERPRISE_PAYMENT_ACCOUNT',
  /** 超级用户 */
  MAX = 'MAX',
}

export const accountTypeLabel = {
  [EAccountType.ACCOUNT_BOOK]: '账簿',
  [EAccountType.PERSONAL_PAYMENT]: '个人支付账户',
  [EAccountType.ENTERPRISE_PAYMENT]: '企业支付账户',
};

/** 银行卡类型 */
export enum EBankType {
  /** 企业对公户 */
  ENTERPRISE = 'ENTERPRISE_BANK_ACCT',
  /** 法人银行卡 */
  LEGALREPT = 'LEGALREPT_BANK_CARD',
  /** 个人银行卡 */
  PERSONAL = 'PERSONAL_BANK_ACCT',
}

export const bankTypeLabel = {
  [EBankType.ENTERPRISE]: '企业对公户',
  [EBankType.LEGALREPT]: '法定代表人银行卡',
  [EBankType.PERSONAL]: '个人银行卡',
};

/** 企业类型 */
export enum ECompanyType {
  /** 个体工商户 */
  Personal = 'PROPRIETOR',
  /** 公司 */
  Company = 'ENTERPRISE',
}

export const companyTypeLabel = {
  [ECompanyType.Personal]: '个体工商户',
  [ECompanyType.Company]: '公司',
};

export enum EOpenBankType {
  /** 新网银行 */
  XWBANK = 'xwbank',
  /** 厦门国际银行 */
  XIB = 'xib',
}

export const accountBankLabel = {
  [EOpenBankType.XWBANK]: '新网银行',
  [EOpenBankType.XIB]: '厦门国际银行',
};

export enum EPayeeIdType {
  /** 身份证 */
  Ssn = 1,
}

export const payeeIdTypeLabel = { [EPayeeIdType.Ssn]: '身份证' };

/** 代理商账户状态 */
export enum EAgentBenefit {
  /** 未配置 */
  UnSetted = 0,
  /** 已配置 */
  Setted = 1,
  /** 无 */
  NoOne = 2,
}

export const agentBenefitLabel = {
  [EAgentBenefit.UnSetted]: '未配置',
  [EAgentBenefit.Setted]: '已配置',
  [EAgentBenefit.NoOne]: '无需配置',
};

export const agentBenefitStyle = {
  [EAgentBenefit.NoOne]: 'info',
  [EAgentBenefit.Setted]: 'success',
  [EAgentBenefit.UnSetted]: 'info',
};

/** 代理商账户状态 */
export enum EAgentAccountStatus {
  /** 停用 */
  Disabled = 0,
  /** 启用 */
  Enabled = 1,
}

export const agentAccountStatusLabel = {
  [EAgentAccountStatus.Disabled]: '已停用',
  [EAgentAccountStatus.Enabled]: '已启用',
};

export const agentAccoutStatusStyle = {
  [EAgentAccountStatus.Disabled]: 'danger',
  [EAgentAccountStatus.Enabled]: 'success',
};

/** 代理商用户状态 */
export enum EAgentUserStatus {
  /** 已冻结 */
  Frozen = 2,
  /** 已启用 */
  Enabled = 1,
  /** 已停用 */
  Disabled = 0,
}

export const agentUserStatusLabel = {
  [EAgentUserStatus.Frozen]: '已冻结',
  [EAgentUserStatus.Enabled]: '已启用',
  [EAgentUserStatus.Disabled]: '已停用',
};

/** 用户中心 - 全部用户 - 操作用户状态 */
export const allOperationUserStatusLabel = {
  [EAgentUserStatus.Frozen]: '已解封',
  [EAgentUserStatus.Enabled]: '已停用',
  [EAgentUserStatus.Disabled]: '已启用',
};

export const agentUserStatusStyle = {
  [EAgentUserStatus.Frozen]: 'danger',
  [EAgentUserStatus.Enabled]: 'success',
  [EAgentUserStatus.Disabled]: 'info',
};

/** 代理商用户激活状态 */
export enum EAgentUserActiveStatus {
  /** 已锁定 */
  Lock = 0,
  /** 已激活 */
  Active = 1,
}

export const agentUserActiveStatusLabel = {
  [EAgentUserActiveStatus.Lock]: '已锁定',
  [EAgentUserActiveStatus.Active]: '已激活',
};

export const agentUserActiveStatusStyle = {
  [EAgentUserActiveStatus.Lock]: 'warning',
  [EAgentUserActiveStatus.Active]: 'success',
};

export interface IRoleOptionsItem {
  /** 角色名称 */
  roleName?: string;
  /** 角色Id */
  roleId?: number;
}

/** 重复手机号、用户名状态码 */
export enum EReponseCode {
  repeatPhoneCode = 1,
  repeatNameCode = 0,
}

/** 支付通道 */
export enum EPaymentChannel {
  /** 招商 */
  ZS = 0,
  /** 连连支付 */
  Lianlian = 1,
  /** 易宝支付 */
  Yibao = 2,
  /** 支付宝 */
  Alipay = 3,
  /** 支付宝记账簿 */
  AlipayAccountBook = 4,
  /** 分账连连 */
  LianlianBill = 5,
}

export const paymentChannelLabel = {
  [EPaymentChannel.ZS]: '招商银行',
  [EPaymentChannel.Lianlian]: '连连支付',
  [EPaymentChannel.Yibao]: '易宝支付',
  [EPaymentChannel.Alipay]: '支付宝',
  [EPaymentChannel.AlipayAccountBook]: '支付宝记账簿',
  [EPaymentChannel.LianlianBill]: '分账连连',
};

export const payChannelLabel = {
  ZS: '招商银行',
  LIANLIAN: '连连支付',
  YEEPAY: '易宝支付',
  ALIPAY: '支付宝',
  ALIPAY_ACCOUNT_BOOK: '支付宝记账簿',
};

export const rcptMethodLabel = {
  QR_CODE: '二维码',
  PUBLIC_WITHDRAWA: '公域提现',
  OPENAPI: '开放API收款',
  POS_TERMINAL: 'POS终端',
  ACCOUNT_BOOK: '记账簿收款',
};

export const txnTypeLabel = {
  WECHAT: '微信支付',
  ALIPAY: '支付宝',
  WITHDRAWAL_POSTED: '提现入账',
  UNIONPAY: '银联云闪付',
  POS: '刷卡支付',
  BANK_TRANSFER: '银行转账',
};

export interface ICompanyQueryParam {
  /** 代理商ID */
  agentId?: number;
  /** 是否启用 0禁用 1启用 */
  enable?: 0 | 1;
  /** 集团企业类型(0非集团企业 1集团企业) */
  groupType?: EGroupType;
  /** 集团户ID */
  groupCompanyId?: number;
  /** 入驻申请状态(3已入驻) */
  settleStatus?: number;
  /** 业务类型(2:saas分账订单独有 3:众包,4:撮合) */
  businessId?: 2 | 3 | 4;
  /** 是否包含草稿(0不包含 1包含)默认为不包含 */
  containDraft?: 0 | 1;
  /** 审批状态 */
  approveStatus?: ECompanyApproveStatus;
}

export interface IAgentsQueryParam {
  /** 上级代理商-agentId */
  parentId?: number;
  /** 代理商类型（0企业代理商 1个人代理商） */
  agentType?: 0 | 1;
  /** 代理商等级 */
  levelType?: EAgentLevel | null;
  /** 是否启用 0禁用 1启 */
  enable?: 0 | 1;
  /** 是否配置阶梯费率(0否 1是) */
  rateConfig?: 0 | 1;
  /** 业务类型(3:众包,4:撮合) */
  businessId?: 3 | 4;
}

export interface IAgentOptionsItem extends IAgentsQueryParam {
  /** id */
  id?: number;
  agentId?: number;
  /** 代理商名称 */
  agentName?: string;
  /** 代理商编号 */
  code?: string;
}

export const paymentChannelClass = {
  [EPaymentChannel.Lianlian]: 'pay-lianlian',
  [EPaymentChannel.Yibao]: 'pay-yibao',
  [EPaymentChannel.Alipay]: 'pay-alipay',
};

export interface IAgentFeeItem {
  /** 费率 */
  costRate: string;
  /** 费率 */
  rate?: string;
  /** 费率描述 */
  name: string;
  /** 代理商等级 */
  agentLevel?: EAgentLevel | null | '' | undefined;
  /** 0:SAAS,1:代理商,2:企业 */
  sysType?: 0 | 1 | 2;
}

/** 提现状态 */
export enum EWithdrawalStatus {
  /** 处理中 */
  Action = 0,
  /** 提现完成 */
  Success = 1,
  /** 提现失败 */
  Failed = 2,
  /** 退汇 */
  Return = 3,
}

export const withdrawalStatusLabel = {
  [EWithdrawalStatus.Action]: '处理中',
  [EWithdrawalStatus.Success]: '提现完成',
  [EWithdrawalStatus.Failed]: '提现失败',
  [EWithdrawalStatus.Return]: '退汇',
};

export const withdrawalStatusStyle = {
  [EWithdrawalStatus.Action]: 'warning',
  [EWithdrawalStatus.Success]: 'success',
  [EWithdrawalStatus.Failed]: 'danger',
  [EWithdrawalStatus.Return]: 'warning',
};

export interface IPageParam {
  /** 每页数量 */
  size: number;
  /** 当前页码 */
  page: number;
}

export interface IEnterpriseItem {
  /** id */
  id: number;
  /** 企业编码 */
  code?: string;
  /** 代理商Id */
  agentId?: number;
  /** 企业名称 */
  companyName: string;
  /** 存在待申请 0否 1是 */
  havePendingApply?: 0 | 1;
  /** 代理商名称 */
  agentName?: string;
}

export interface IAgentMergeInfo {
  /** 设置代理商 */
  agentBase: IAgentBase;
  /** 基本信息 */
  agentContacts: IContactDetail;
  /** 营业执照信息 */
  agentBusiness: ILicenseDetail;
  /** 法人信息 */
  agentLegal: ILegalDetail;
  /** 开户许可证信息 */
  agentLicense: IAccOpeningDetail;
  /** 个人代理商基本信息 */
  agentPerson?: IAgentPersonDetail;
  /** 支付信息 */
  agentPay?: { payPhone: string };
  /** 套餐 */
  businessMenuIds?: number[];
}

export interface IAgentPay {
  /** 支付电话 */
  payPhone: string;
}

export interface IServerBase {
  /** 服务商id */
  id: number;
  /** 服务商编码 */
  code: string;
  /** 是否开启0关闭1开启 */
  enable: string;
  /** 服务商名称 */
  serverName: string;
}

export interface IServerMergeInfo {
  /** 代理商 */
  agentPay: IAgentPay;
  /** 服务商-营业执照信息 */
  serverBusiness: ILicenseDetail;
  /** 服务商-基本信息 */
  serverContacts: IContactDetail;
  /** 服务商-设置 */
  serverBase: IServerBase;
  /** 服务商-开户许可证信息 */
  serverLicense: IAccOpeningDetail;
  /** 服务商-法人信息 */
  serverLegal: ILegalDetail;
  /** 菜单权限 */
  businessMenuIds: number[];
  /** 产品权限 */
  productProxyType: number[];
  /** 服务商类型 */
  serverType: EServerType;
}

/** 服务商账户状态 */
export enum EServerAccountStatus {
  /** 停用 */
  Disabled = 0,
  /** 启用 */
  Enabled = 1,
}

export const serverAccountStatusLabel = {
  [EServerAccountStatus.Disabled]: '已停用',
  [EServerAccountStatus.Enabled]: '已启用',
};

export const serverAccoutStatusStyle = {
  [EServerAccountStatus.Disabled]: 'danger',
  [EServerAccountStatus.Enabled]: 'success',
};

/** 服务商受益人 */
export enum EServerBenefit {
  /** 未配置 */
  UnSetted = 0,
  /** 已配置 */
  Setted = 1,
  /** 无 */
  NoOne = 2,
}

export const serverBenefitLabel = {
  [EServerBenefit.NoOne]: '无需配置',
  [EServerBenefit.UnSetted]: '未配置',
  [EServerBenefit.Setted]: '已配置',
};

export const serverBenefitStyle = {
  [EServerBenefit.UnSetted]: 'info',
  [EServerBenefit.Setted]: 'success',
  [EServerBenefit.NoOne]: 'info',
};

export interface IServerOptionsItem {
  /** id */
  id?: number;
  /** 服务商名称 */
  serverName?: string;
  /** 服务商编号 */
  code?: string;
  /** 是否开启(0关闭 1开启) */
  enable?: number;
  /** 是否需要实名认证(0不需要1需要) */
  isAuth?: number;
  /** 实名状态 0:未实名 1:已实名 */
  authStatus?: number;
}

export interface ICompanyMergeInfo {
  /**
   * 设置代理商
   * @description {@link ICompany}
   */
  companyBase: ICompany;
  /**
   * 基本信息
   * @description {@link IContactDetail}
   */
  companyContacts: IContactDetail;
  /**
   * 营业执照信息
   * @description {@link ILicenseCompanyDetail}
   */
  companyBusiness: ILicenseCompanyDetail;
  /**
   * 法人信息
   * @description {@link ILegalDetail}
   */
  companyLegal: ILegalDetail;
  /**
   * 开户许可证信息
   * @description {@link ICompanyAccDetail}
   */
  companyLicense: ICompanyAccDetail;
  /**
   * 公司介绍
   * @description {@link ICompanyIntroduce}
   */
  companySettled?: ICompanyIntroduce;
  /** 安全管理 */
  companyPay?: { payPhone: string; smsVerify: boolean };
  /**
   * 集团户
   * @description {@link ICompanyGroup}
   */
  companyGroup?: ICompanyGroup;
  /** 菜单权限 */
  selectedIds?: number[];
  /** 业务权限 */
  businessMenuIds?: number[];
  /** 申请类型 */
  applicationType?: 'add' | 'edit';
  /** 申请备注 */
  remark?: string;
  /** 董监高信息 */
  seniorExecutiveNames?: string[];
  /** 风险等级 */
  riskLevel?: ERiskLevel;
}

export interface ICompanyChangeRecord {
  /** 变更前 */
  pre?: ICompanyMergeInfo;
  /** 变更后 */
  post?: ICompanyMergeInfo;
}

export interface ICompanyGroup {
  /**
   * 是否有集团邀请(0无邀请 1有邀请)
   * @description {@link EGroupInviteType}
    */
  haveGroupInvite?: EGroupInviteType;
  /**
   * 申请类型
   * @description {@link EGroupApplyType}
   */
  groupApplyType?: EGroupApplyType;
  /** 集团企业名称 */
  companyGroupName?: string;
  /** 所属集团统一社会信用代码 */
  groupCreditCode?: string;
  /** 驳回备注 */
  remark?: string;
}

export enum EGroupApplyType {
  /** 未申请 */
  None = 0,
  /** 申请成为集团子企业 */
  ToGroupSubCompany = 1,
  /** 申请成为集团子企业（被驳回） */
  ToSubCompanyRejected = 2,
  /** 申请成为集团 */
  ToGroup = 3,
  /** 申请成为集团（被驳回） */
  ToGroupRejected = 4,
}

export enum ECompanyGroupType {
  /** 普通企业 (可用于初始状态判断) */
  Company = 0,
  /** 集团户 */
  Group = 1,
  /** 集团子企业 */
  GroupSubCompany = 2,
}

export enum EGroupInviteType {
  /** 无邀请 */
  None = 0,
  /** 有邀请 */
  Has = 1,
}

export interface ICompany {
  /**
   * 代理商类型
   * @description {@link ECompanyType}
   */
  type?: ECompanyType;
  /** 代理商名称 */
  agentName?: string;
  /** 代理商Id */
  agentId?: number;
  /** 销售名称 */
  businessUserName?: string;
  /** 销售ID */
  businessUserId?: number;
  /** 企业id */
  id?: number;
  /**
   * 企业类型
   */
  companyGroupType?: ECompanyGroupType;
  /** 集团户企业id */
  companyGroupId?: number;
  /** 集团企业名称 */
  companyGroupName?: string;
  /** 所属集团统一社会信用代码 */
  groupCreditCode?: string;
  /** 审核状态 */
  approveStatus?: ECompanyApproveStatus;
  /** 驳回原因 */
  rejectReason?: string;
  /** 企业编号 */
  code?: string;
  /** 企业名称 */
  companyName?: string;
  /** 草稿id */
  draftId?: number;
  groupOperateAuthStatus?: EGroupOperateAuthStatus;
}

/** 代理商用户激活状态 */
export enum EGroupType {
  /** 非集团户 */
  No = 0,
  /** 集团户 */
  Yes = 1,
}

export interface IAgentBase {
  /** 上级代理商Id */
  parentId?: number;
  /** 代理商等级 */
  levelType?: EAgentLevel;
  /** 代理类型 */
  agentType?: EAgentType;
  /** 企业类型 */
  companyType?: ECompanyAgentType;
  /** 上级代理商 */
  parentAgentName?: number;
  /** 代理商名称 */
  agentName?: string;
  /** 代理商Id */
  agentId?: number;
  /** 销售Id */
  businessUserId?: number;
  /** 销售名称 */
  businessUserName?: string;
  /** 分润扣点 */
  agentRate?: string;
}
export interface IContactDetail extends IContactPerson {
  /** 联系人地址 */
  contactAddress?: string;
  /** 联系人微信号 */
  weChatNo?: string;
  /** 联系人市ID */
  cityRegionId?: number;
  /** 联系人区ID */
  regionId?: number;
  /** 单位省份 */
  provinceRegionId?: number;
  /** 单位省市区 */
  regionName?: string;
  /** 登陆手机号 */
  loginPhone?: string;
  /** 是否强制实名 */
  isAuth?: EAuthType;
}

export type ILicenseDetail =
  Partial<
    Pick<
      ILicenseInfo,
      | 'businessLicensePic'
      | 'name'
      | 'legalName'
      | 'address'
      | 'creditCode'
      | 'businessLicenseStartDate'
      | 'businessLicenseEndDate'
      | 'businessLicenseExpiryDate'
      | 'businessScope'
      | 'shortName'
    >
  >;

export interface ILicenseCompanyDetail extends ILicenseDetail {
  /** 公司简介 */
  companyProfile: string;
  /** 业务场景 */
  companyScene: string;
  /** 企业简称 */
  shortName?: string;
}

export type ILegalDetail =
  Partial<
    Pick<
      ILeagalPerson,
      | 'idNoFrontUrl'
      | 'idNoBackUrl'
      | 'legalName'
      | 'legalPhone'
      | 'legalIdNo'
      | 'idNoExpiryStartDate'
      | 'idNoExpiryEndDate'
      | 'idNoExpiryDate'
    >
  >;

export interface IAccOpeningDetail {
  /**
   * 开户许可证照片
   * @description ECompanyType.Company
   */
  licenseUrl?: string;
  /**
   * 法定代表人银行卡照片
   * @description ECompanyType.Personal
   */
  bankCardUrl?: string;
  /**
   * 0 法人   1对公
   */
  licenseType?: 1 | 0;
  /** 开户银行 */
  bankName: string;
  /**
   * 电话号码
   * @description ECompanyType.Company
   */
  phoneNo?: string;
  /**
   * 银行预留手机号
   * @description ECompanyType.Personal
   */
  bankPhone?: string;
  /**
   * 企业基本账号
   * @description ECompanyType.Company
   */
  bankAccount?: string;
  /**
   * 银行账号
   * @description ECompanyType.Personal
   */
  personBankAccount?: string;
  /** 纳税识别号 */
  creditCode: string;
  /** 对公账户名 */
  accountName?: string;
  /** 企业营业地址 */
  address?: string;
}

export interface IAgentPersonDetail {
  /** 姓名 */
  personName: string;
  /** 身份证号 */
  idNo: string;
  /** 身份证正面照片 */
  idNoFront: string;
  /** 身份证背面照片 */
  idNoBack: string;
  /** 身份证有效期开始 */
  idNoExpiryStartDate: string;
  /** 身份证有效期结束 */
  idNoExpiryEndDate: string;
  /** 账号户名 */
  accountName: string;
  /** 银行卡号 */
  bankCard: string;
  /** 开户银行 */
  bankName: string;
  /** 开户支行 */
  bankBranch: string;
  /** 登陆手机号 */
  loginPhone: string;
  /** 代理商简称 */
  shortName: string;
  /** 微信号 */
  weChatNo: string;
  /** 银行卡照片 */
  bankCardPhoto: string;
  /** 是否强制实名 */
  isAuth?: EAuthType;
  /** 联系人地址 */
  address?: string;
}

export interface ICompanyAccDetail extends IAccOpeningDetail {
  /** 账户类型 */
  licenseType?: EBindingType;
  /** 法人银行卡url */
  bankCardUrl?: string;
  /** 法人银行卡预留手机号 */
  bankPhone?: string;
  /** 法人银行卡账户 */
  personBankAccount?: string;
}

export interface ICompanyIntroduce {
  /** 公司简介 */
  companyProfile: string;
  /** 业务场景 */
  companyScene: string;
  /** 《企业入驻表》 */
  settledList?: IEntryFormItem[];
  /** 上传企业门头照 */
  caseList?: IEntryFormItem[];
  /** 上传办公场所照 */
  officeList?: IEntryFormItem[];
  /** 上传业务场景照 */
  sceneList?: IEntryFormItem[];
}

export interface IEntryFormItem {
  /** 文件名 */
  name: string;
  fileName?: string;
  /** 文件url */
  fileUrl?: string;
  url: string;
  /** 文件类型 */
  uid?: number;
}

export interface IWithdraw {
  /** 账户连连id */
  lianId?: number;
  /** 账户编号 */
  lianNo?: string;
  /** 银行账号 */
  bankNo?: string;
  /** 手机号 */
  regPhone?: string;
  /** 企业通道配置ID */
  companyChannelConfigId?: string;
  /** 可用总金额（元） */
  availableBalance?: number;
}

/** 分账账户表单第3步 */
export interface IAccountApplySubmit {
  /** 连连Id */
  lianId?: number;
  /** 验证手机号 */
  regPhone?: string;
  /** 验证码 */
  code?: string;
  /** 支付密码 */
  payPassword?: string;
  /** 随机因子 */
  randomKey?: string;
}

export interface IUserRoleDetail {
  /** 菜单树信息 */
  menuData?: IRoleMenuItem[];
  /** 已勾选菜单 */
  selectedIds?: number[];
  /** 数据权限 */
  dataPermission?: ERoleDepartmentPermission;
  /** 部门列表 */
  deptList?: number[];
}

export enum ERoleType {
  /** 系统角色 */
  SYS = 0,
  /** 自定义角色 */
  CUSTOM = 1,
}

export interface IRoleTreeItem {
  /** 角色id */
  roleId?: number;
  /** 角色名称 */
  roleName?: string;
  /** 角色等级(0其他角色1超级管理员) */
  level?: string;
  /** 端类型(0:saas 1:代理商 2:企业) */
  systemType?: ESystemType;
  /** 角色类型(0:SYS 1:CUSTOM) */
  roleType?: ERoleType;
}

export interface IRoleMenuItem {
  /** id */
  menuId?: number;
  /** 组件名称 */
  meta?: { title: string };
  /** 子组件 */
  children?: IRoleMenuItem[];
}

export interface IPaymentChannel {
  /** 通道 */
  id: EPaymentChannel;
  /** 通道名称 */
  name: string;
  /** 通道状态 */
  enabled: boolean;
  /** 费率 */
  rate: number;
  /** 可用银行卡 */
  availableBank?: number;
}

export interface IpassRec {
  /** token */
  token?: string;
  /** txnSeqNo */
  txnSeqno?: string;
  /** 金额 */
  totalAmount?: number;

}

export interface IBusinessAuthInfo {
  /** 业务id */
  businessId: number;
  /** 业务名称 */
  businessName: string;
  /** 业务权限id */
  businessMenuId: number;
  /** 菜单权限id集合 */
  menuIds: number[];
  /** 菜单树信息 */
  menuData: IRoleMenuItem[];
  /** 是否选中（0：否，1：是） */
  isSelected: number;
  /** 是否默认功能（0：否，1：是） */
  isDefault: number;
  /** 当前选中状态 unchecked: 未选中 checked: 已选中 indeterminate: 部分选中 */
  checkedStatus: 'unchecked' | 'checked' | 'indeterminate';
}

export interface IUpdateBusinessAuth {
  /** 企业id列表 */
  companyIds?: number[];
  /** 代理商id列表 */
  agentIds?: number[];
  /** 操作类型: 0-新增 1-删除 */
  operator: 0 | 1;
  /** 业务配置 */
  businessList: IBusinessAuthInfo[];
}

/**
 * 账户管理 - 业务类型
*/
export enum EBusinessType {
  /** 特级代理商 */
  SuperAgent = 1,
  /** 一级代理商 */
  FirstAgent = 2,
  /** 二级代理商 */
  SecondAgent = 3,
  /** 集团企业 */
  GroupCompany = 4,
  /** 企业 */
  Company = 5,
  /** 服务商 */
  Server = 6
}

export enum EChannelStatus {
  /** 启用 */
  ENABLE = 'ENABLE',
  /** 禁用 */
  DISABLE = 'DISABLE',
}

export const channelStatusLabel = {
  [EChannelStatus.ENABLE]: '启用',
  [EChannelStatus.DISABLE]: '禁用',
};

export const channelStatusStyle = {
  [EChannelStatus.ENABLE]: 'success',
  [EChannelStatus.DISABLE]: 'danger',
};

/** 公域收款开通状态 */
export enum EPublicCollectionStatus {
  /** 待申请 */
  DRAFT = 'DRAFT',
  /** 待提交 */
  PENDING_SUBMIT = 'PENDING_SUBMIT',
  /** 审核中（产品变更审核中） */
  PRODUCT_MODIFY_REVIEWING = 'PRODUCT_MODIFY_REVIEWING',
  /** 审核中（业务开通中） */
  PRODUCT_MODIFY_BUSINESS_OPENING = 'PRODUCT_MODIFY_BUSINESS_OPENING',
  /** 审核中（产品变更成功） */
  PRODUCT_MODIFY_COMPLETED = 'PRODUCT_MODIFY_COMPLETED',
  /** 审核中（开立银行账户受理中） */
  BANK_OPENING_PROCESS = 'BANK_OPENING_PROCESS',
  /** 待签署 */
  PRODUCT_MODIFY_AGREEMENT_SIGNING = 'PRODUCT_MODIFY_AGREEMENT_SIGNING',
  /** 申请失败（产品变更审核驳回） */
  PRODUCT_MODIFY_REVIEW_BACK = 'PRODUCT_MODIFY_REVIEW_BACK',
  /** 申请失败（开立银行账户失败） */
  BANK_OPENING_FAIL = 'BANK_OPENING_FAIL',
  /** 已开通（开立银行账户成功） */
  BANK_OPENING_SUCCESS = 'BANK_OPENING_SUCCESS',
  /** 待验证 */
  BANK_OPENING_SMS_AUTH = 'BANK_OPENING_SMS_AUTH',
}

export const publicCollectionLabel = {
  [EPublicCollectionStatus.DRAFT]: '待申请',
  [EPublicCollectionStatus.PENDING_SUBMIT]: '待提交',
  [EPublicCollectionStatus.PRODUCT_MODIFY_REVIEWING]: '审核中',
  [EPublicCollectionStatus.PRODUCT_MODIFY_BUSINESS_OPENING]: '审核中',
  [EPublicCollectionStatus.PRODUCT_MODIFY_COMPLETED]: '审核中',
  [EPublicCollectionStatus.BANK_OPENING_PROCESS]: '审核中',
  [EPublicCollectionStatus.PRODUCT_MODIFY_AGREEMENT_SIGNING]: '开通中',
  [EPublicCollectionStatus.PRODUCT_MODIFY_REVIEW_BACK]: '申请失败',
  [EPublicCollectionStatus.BANK_OPENING_FAIL]: '申请失败',
  [EPublicCollectionStatus.BANK_OPENING_SUCCESS]: '已开启',
  [EPublicCollectionStatus.BANK_OPENING_SMS_AUTH]: '待验证',
};

export const publicCollectionSyle = {
  [EPublicCollectionStatus.DRAFT]: 'primary',
  [EPublicCollectionStatus.PENDING_SUBMIT]: 'primary',
  [EPublicCollectionStatus.PRODUCT_MODIFY_REVIEWING]: 'primary',
  [EPublicCollectionStatus.PRODUCT_MODIFY_BUSINESS_OPENING]: 'primary',
  [EPublicCollectionStatus.PRODUCT_MODIFY_COMPLETED]: 'primary',
  [EPublicCollectionStatus.BANK_OPENING_PROCESS]: 'primary',
  [EPublicCollectionStatus.PRODUCT_MODIFY_AGREEMENT_SIGNING]: 'primary',
  [EPublicCollectionStatus.PRODUCT_MODIFY_REVIEW_BACK]: 'danger',
  [EPublicCollectionStatus.BANK_OPENING_FAIL]: 'danger',
  [EPublicCollectionStatus.BANK_OPENING_SUCCESS]: 'success',
  [EPublicCollectionStatus.BANK_OPENING_SMS_AUTH]: 'primary',
};

/** 线下收单开通状态 */
export enum EOfflineReceiptStatus {
  /** 待申请 */
  DRAFT = 'DRAFT',
  /** 待提交 */
  PENDING_SUBMIT = 'PENDING_SUBMIT',
  /** 审核中(产品变更审核中) */
  PRODUCT_MODIFY_REVIEWING = 'PRODUCT_MODIFY_REVIEWING',
  /** 申请失败(产品变更审核驳回) */
  PRODUCT_MODIFY_REVIEW_BACK = 'PRODUCT_MODIFY_REVIEW_BACK',
  /** 待签署（协议签署中） */
  PRODUCT_MODIFY_AGREEMENT_SIGNING = 'PRODUCT_MODIFY_AGREEMENT_SIGNING',
  /** 审核中（业务开通中） */
  PRODUCT_MODIFY_BUSINESS_OPENING = 'PRODUCT_MODIFY_BUSINESS_OPENING',
  /** 待认证（产品变更成功，待线下收款账户认证） */
  PRODUCT_MODIFY_COMPLETED = 'PRODUCT_MODIFY_COMPLETED',
  /** 待认证（渠道未授权，待线下收款账户认证） */
  AUTHORIZE_STATE_UNAUTHORIZED = 'AUTHORIZE_STATE_UNAUTHORIZED',
  /** 待认证（微信渠道已授权） */
  AUTHORIZE_STATE_WECHAT_AUTHORIZED = 'AUTHORIZE_STATE_WECHAT_AUTHORIZED',
  /** 待认证（支付宝渠道已授权） */
  AUTHORIZE_STATE_ALIPAY_AUTHORIZED = 'AUTHORIZE_STATE_ALIPAY_AUTHORIZED',
  /** 认证成功（产品变更成功） */
  AUTHORIZE_STATE_AUTHORIZED = 'AUTHORIZE_STATE_AUTHORIZED',
}

export const offlineReceiptLabel = {
  [EOfflineReceiptStatus.DRAFT]: '待申请',
  [EOfflineReceiptStatus.PENDING_SUBMIT]: '待申请',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_REVIEWING]: '审核中',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_BUSINESS_OPENING]: '审核中',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_REVIEW_BACK]: '申请失败',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_AGREEMENT_SIGNING]: '开通中',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_COMPLETED]: '待认证',
  [EOfflineReceiptStatus.AUTHORIZE_STATE_UNAUTHORIZED]: '待认证',
  [EOfflineReceiptStatus.AUTHORIZE_STATE_WECHAT_AUTHORIZED]: '待认证',
  [EOfflineReceiptStatus.AUTHORIZE_STATE_ALIPAY_AUTHORIZED]: '待认证',
  [EOfflineReceiptStatus.AUTHORIZE_STATE_AUTHORIZED]: '已开启',
};

export const offlineReceiptSyle = {
  [EOfflineReceiptStatus.DRAFT]: 'primary',
  [EOfflineReceiptStatus.PENDING_SUBMIT]: 'primary',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_REVIEWING]: 'primary',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_REVIEW_BACK]: 'danger',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_AGREEMENT_SIGNING]: 'primary',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_BUSINESS_OPENING]: 'primary',
  [EOfflineReceiptStatus.PRODUCT_MODIFY_COMPLETED]: 'primary',
  [EOfflineReceiptStatus.AUTHORIZE_STATE_UNAUTHORIZED]: 'primary',
  [EOfflineReceiptStatus.AUTHORIZE_STATE_WECHAT_AUTHORIZED]: 'primary',
  [EOfflineReceiptStatus.AUTHORIZE_STATE_ALIPAY_AUTHORIZED]: 'primary',
  [EOfflineReceiptStatus.AUTHORIZE_STATE_AUTHORIZED]: 'success',
};

/** 入网商户业务角色 */
export enum EMerchantBusinessRole {
  /** 代理商 */
  PLATFORM_MERCHANT = 'PLATFORM_MERCHANT',
  /** 企业 */
  SETTLED_MERCHAN = 'SETTLED_MERCHAN',
}

/** 产品类别 */
export enum EProductClass {
  /** 公域收款 */
  PUBLIC_COLLECTION = 'PUBLIC_COLLECTION',
  /** 线下收单 */
  OFFLINE_RECEIPT = 'OFFLINE_RECEIPT',
  /** 线下收单-微信 */
  OFFLINE_RECEIPT_WECHAT = 'OFFLINE_RECEIPT_WECHAT',
  /** 线下收单-支付宝 */
  OFFLINE_RECEIPT_ALIPAY = 'OFFLINE_RECEIPT_ALIPAY',
}

export const productClassLabel = {
  [EProductClass.PUBLIC_COLLECTION]: '公域收款',
  [EProductClass.OFFLINE_RECEIPT]: '线下收单',
  [EProductClass.OFFLINE_RECEIPT_WECHAT]: '线下收单-微信',
  [EProductClass.OFFLINE_RECEIPT_ALIPAY]: '线下收单-支付宝',
};

export const productClass = [
  {
    label: productClassLabel[EProductClass.PUBLIC_COLLECTION],
    value: EProductClass.PUBLIC_COLLECTION,
  },
  {
    label: productClassLabel[EProductClass.OFFLINE_RECEIPT],
    value: EProductClass.OFFLINE_RECEIPT,
  },
  {
    label: productClassLabel[EProductClass.OFFLINE_RECEIPT_WECHAT],
    value: EProductClass.OFFLINE_RECEIPT_WECHAT,
  },
  {
    label: productClassLabel[EProductClass.OFFLINE_RECEIPT_ALIPAY],
    value: EProductClass.OFFLINE_RECEIPT_ALIPAY,
  },
];

/**
 * 系统配置 - 是否默认功能
*/
export enum EBusinessStatus {
  /** 是 */
  Yes = 1,
  /** 否 */
  No = 0,
}

/** 产品配置 - 通道配置状态 */
export enum EChannelConfigStatus {
  /** 未配置 */
  Not = 0,
  /** 已配置 */
  Yes = 1,
}

export const channelConfigLabel = {
  [EChannelConfigStatus.Not]: '未配置',
  [EChannelConfigStatus.Yes]: '已配置',
};

export enum EPublicOpenbankType {
  /** 新网银行 */
  XWB_Z = 'XWB_Z',
  /** 苏商银行 */
  SUNINGBANK_MULTICHANNEL = 'SUNINGBANK_MULTICHANNEL',
  /** 蓝海银行 */
  WHLHB = 'WHLHB',
}

export const publicOpenbankTypeLabel = {
  [EPublicOpenbankType.XWB_Z]: '新网银行',
  [EPublicOpenbankType.SUNINGBANK_MULTICHANNEL]: '苏商银行',
  [EPublicOpenbankType.WHLHB]: '蓝海银行',
};

export const publicOpenbankList = [
  {
    label: publicOpenbankTypeLabel[EPublicOpenbankType.WHLHB],
    value: EPublicOpenbankType.WHLHB,
  },
  {
    label: publicOpenbankTypeLabel[EPublicOpenbankType.SUNINGBANK_MULTICHANNEL],
    value: EPublicOpenbankType.SUNINGBANK_MULTICHANNEL,
  },
  {
    label: publicOpenbankTypeLabel[EPublicOpenbankType.XWB_Z],
    value: EPublicOpenbankType.XWB_Z,
  },
];

export enum EPersonalAuthType {
  /** 实名手机号认证 */
  MOBILE = 'mobile',
  /** 人脸识别认证 */
  FACE = 'face',
  /** 个人银行卡认证 */
  BANK = 'bank',
  /** 人工审核认证 */
  OFFLINE = 'offline',
}

export const personalAuthTypeConfigLabel = {
  [EPersonalAuthType.MOBILE]: '实名手机号认证',
  [EPersonalAuthType.FACE]: '人脸识别认证',
  [EPersonalAuthType.BANK]: '个人银行卡认证',
  [EPersonalAuthType.OFFLINE]: '人工审核认证',
};

export enum ECertType {
  /** 身份证 */
  ID_CARD = 'id_card',
  /** 港澳居民居住证 */
  HK_MAC_RP = 'hk_mac_rp',
  /** 台湾居民居住证 */
  TAIWAN_RP = 'taiwan_rp',
  /** 外国人永久居留身份证 */
  FOREIGN_PRC = 'foreign_prc',
  /** 护照 */
  PASSPROT = 'passport',
  /** 港澳居民来往内地通行证 */
  HK_MACAO = 'hk_macao',
  /** 台湾居民来往大陆通行证 */
  TAIWAN = 'taiwan',
}

export const certTypeConfigLabel = {
  [ECertType.ID_CARD]: '身份证',
  [ECertType.HK_MAC_RP]: '港澳居民居住证',
  [ECertType.TAIWAN_RP]: '台湾居民居住证',
  [ECertType.FOREIGN_PRC]: '外国人永久居留身份证',
  [ECertType.PASSPROT]: '护照',
  [ECertType.HK_MACAO]: '港澳居民来往内地通行证',
  [ECertType.TAIWAN]: '台湾居民来往大陆通行证',
};

export interface IProductUsageConfig {
  /** 通道id */
  channelId: EPaymentChannel;
  /** 通道名称 */
  channelName: string;
  /** 是否启用 */
  enabled?: boolean;
  /** 数据列表 */
  dataList: IChannelConfigList[];
}

export enum EAuthStatus {
  /** 已实名 */
  Yes = 1,
  /** 未实名 */
  No = 0,
  /** 实名中 */
  Ing = 2,
}

export const authStatusLabel = {
  [EAuthStatus.Yes]: '已实名',
  [EAuthStatus.No]: '未实名',
  [EAuthStatus.Ing]: '实名中',
};

export const authStatusStyle = {
  [EAuthStatus.Yes]: 'success',
  [EAuthStatus.No]: 'info',
  [EAuthStatus.Ing]: 'primary',
};

export enum EAuthType {
  /** 强制 */
  Yes = 1,
  /** 非强制 */
  No = 0,
}

export const authTypeLabel = {
  [EAuthStatus.Yes]: '强制',
  [EAuthStatus.No]: '非强制',
};

export enum ESharingType {
  /** 比例分润 */
  Pro = 1,
  /** 固定值分润 */
  Fixed = 2,
}

export const sharingTypeLabel = {
  [ESharingType.Pro]: '比例分润',
  [ESharingType.Fixed]: '固定值分润',
};

export enum ESceneType {
  /** 线上收款 */
  ONLINE = 'ONLINE',
  /** 线下收款 */
  OFFLINE = 'OFFLINE',
}

export enum ESplitAccountType {
  /** 余额分账 */
  BALANCE = 'BALANCE',
  /** 订单分账 */
  VERB = 'VERB',
}

export const splitAccountLabel = {
  [ESplitAccountType.VERB]: '订单分账',
  [ESplitAccountType.BALANCE]: '余额分账',
};

export enum EBoardSelectTime {
  /** 昨日 */
  YESTERDAY = 'YESTERDAY',
  /** 近7日 */
  WEEK = 'WEEK',
  /** 近30日 */
  MONTH = 'MONTH',
}

export const boardTimeLabel = {
  [EBoardSelectTime.YESTERDAY]: '昨日',
  [EBoardSelectTime.WEEK]: '近7日',
  [EBoardSelectTime.MONTH]: '近30日',
};

/* 看板聚会账户状态 */
export const accountStatusLabel = {
  ...lianCardStatusLabel,
  ...yibaoAccountStatusLabel,
};

export const accountStatusStyle = {
  ...lianCardStatusStyle,
  ...yibaoAccountStatusStyle,
};

/** 众包灵工产品状态 */
export enum EProductServerStatus {
  /** 未配置 */
  NotConfig = 0,
  /** 已启用 */
  Enabled = 1,
  /** 已停用 */
  Disabled = 2,
}

export const productServerStatusLabel = {
  [EProductServerStatus.NotConfig]: '未配置',
  [EProductServerStatus.Enabled]: '已启用',
  [EProductServerStatus.Disabled]: '已停用',
};

export const productServerStatusStyle = {
  [EProductServerStatus.NotConfig]: 'info',
  [EProductServerStatus.Disabled]: 'danger',
  [EProductServerStatus.Enabled]: 'success',
};

/** 众包灵工产品状态 */
export enum EProductChargeMethod {
  /** 以发放金额计算 */
  Grant = 0,
  /** 以开票金额计算 */
  Invoicing = 1,
}

export const productChargeMethodLabel = {
  [EProductChargeMethod.Grant]: '以发放金额计算',
  [EProductChargeMethod.Invoicing]: '以开票金额计算',
};

/** 众包灵工授权状态 */
export enum EAgentAuthorize {
  /** 未授权 */
  Disabled = 0,
  /** 已授权 */
  Enabled = 1,
}

export const agentAuthorizeLabel = {
  [EAgentAuthorize.Disabled]: '未授权',
  [EAgentAuthorize.Enabled]: '已授权',
};

export const agetnAuthorizeStyle = {
  [EAgentAuthorize.Disabled]: 'danger',
  [EAgentAuthorize.Enabled]: 'success',
};

/** 众包灵工授权状态 */
export enum ERateAllocationMethod {
  /** 单一费率 */
  Only = 0,
  /** 阶梯费率 */
  Stairs = 1,
}

export const rateAllocationLabel = {
  [ERateAllocationMethod.Only]: '单一费率',
  [ERateAllocationMethod.Stairs]: '阶梯费率',
};

export interface IIdentityItem {
  /** 用户id */
  userId?: number;
  /** 用户名称 */
  name?: string;
  /** 身份id */
  identityId?: number;
  /** 是否为超级管理员账号(0:不是,1:是) */
  isAdmin?: number;
  /** 用户状态 */
  enabled?: EAgentUserStatus;
  /** 激活状态 */
  activated?: EAgentUserActiveStatus;
}

export interface IIdentityParam {
  /** 用户状态 */
  enabled?: EAgentUserStatus;
  /** 激活状态 */
  activated?: EAgentUserActiveStatus;
}

/** 审批类型 */
export enum EApproveType {
  /** 众包报名 */
  CROWD_REGISTRATION = 1,
  /** 众包订单 */
  CROWD_ORDER = 2,
  /** 众包创建任务 */
  CROWD_DUTY = 3,
  /** 众包任务交付物 */
  CROWD_DELIVER = 4,
  /** 众包发票申请 */
  CROWD_INVOICE = 5,
  /** 预开发票申请 */
  PRE_INVOICE = 6,
  /** 小程序授权 */
  APPLET_PUB = 7,
  /** 创建企业 */
  CREATE_COMPANY = 8,
  /** 企业入驻海南税源地 */
  TAX_SOURCE = 9,
  /** 企业修改开票限制 */
  EDIT_TAX_SOURCE = 10,
  /** 撮合报名 */
  MATCH_REGISTRATION = 11,
  /** 撮合订单 */
  MATCH_ORDER = 12,
  /** 撮合创建任务 */
  MATCH_DUTY = 13,
  /** 众包完税开票 */
  CROWD_TAX_INVOICE = 14,
  /** 撮合完税开票 */
  MATCH_TAX_INVOICE = 15,
  /** 撮合发票申请 */
  MATCH_INVOICE = 16,
  /** 撮合任务交付物 */
  MATCH_DELIVER = 17,
  /** 修改企业 */
  EDIT_COMPANY = 18,
  /** 新增分润任务 */
  CREATE_DISTRIBUTION_TASK = 19,
  /** 支付宝记账簿 */
  ALIPAY_ACCOUNT_BOOK = 20,
  /** 服务商入驻海南税源地 */
  SERVER_TAX_SOURCE = 21,
  /** 服务商修改开票限制 */
  SERVER_EDIT_TAX_SOURCE = 22,
  /** 众包完税线下开票 */
  CROWD_TAX_OFFLINE_INVOICE = 23,
}

export const approveTypeLabel = {
  [EApproveType.CROWD_REGISTRATION]: '众包报名',
  [EApproveType.CROWD_ORDER]: '众包订单',
  [EApproveType.CROWD_DUTY]: '众包创建任务',
  [EApproveType.CROWD_DELIVER]: '众包任务交付物',
  [EApproveType.CROWD_INVOICE]: '众包发票申请',
  [EApproveType.PRE_INVOICE]: '预开发票申请',
  [EApproveType.APPLET_PUB]: '小程序授权',
  [EApproveType.CREATE_COMPANY]: '创建企业',
  [EApproveType.TAX_SOURCE]: '企业入驻海南税源地',
  [EApproveType.EDIT_TAX_SOURCE]: '企业修改开票限制',
  [EApproveType.MATCH_REGISTRATION]: '撮合报名',
  [EApproveType.MATCH_ORDER]: '撮合订单',
  [EApproveType.MATCH_DUTY]: '撮合创建任务',
  [EApproveType.CROWD_TAX_INVOICE]: '众包完税开票',
  [EApproveType.MATCH_TAX_INVOICE]: '撮合完税开票',
  [EApproveType.MATCH_INVOICE]: '撮合发票申请',
  [EApproveType.MATCH_DELIVER]: '撮合任务交付物',
  [EApproveType.EDIT_COMPANY]: '修改企业',
  [EApproveType.CREATE_DISTRIBUTION_TASK]: '新增分润任务',
  [EApproveType.ALIPAY_ACCOUNT_BOOK]: '支付宝记账簿',
  [EApproveType.SERVER_TAX_SOURCE]: '服务商入驻海南税源地',
  [EApproveType.SERVER_EDIT_TAX_SOURCE]: '服务商修改开票限制',
  [EApproveType.CROWD_TAX_OFFLINE_INVOICE]: '众包完税线下开票',
};

/** 审批节点 */
export enum EApproveStep {
  /** 初审 */
  FIRST = 1,
  /** 终审 */
  FINAL = 2,
}

export const approveStepLabel = {
  [EApproveStep.FIRST]: '初审',
  [EApproveStep.FINAL]: '终审',
};

/** 审批状态 */
export enum EApproveStatus {
  /** 待审核 */
  PENDING = 1,
  /** 通过 */
  SUCCESS = 2,
  /** 驳回 */
  REJECT = 3,
  /** 终止 */
  TERMINATE = 4,
}

export const approveStatusLabel = {
  [EApproveStatus.PENDING]: '待审核',
  [EApproveStatus.SUCCESS]: '通过',
  [EApproveStatus.REJECT]: '驳回',
  [EApproveStatus.TERMINATE]: '终止',
};

export const approveStatusStyle = {
  [EApproveStatus.PENDING]: 'primary',
  [EApproveStatus.SUCCESS]: 'success',
  [EApproveStatus.REJECT]: 'danger',
  [EApproveStatus.TERMINATE]: 'danger',
};

/** 小程序管理审批状态 */
export enum EAppletAuditStatus {
  /** 暂未绑定 */
  NOT_BIND = 0,
  /** 待发布 */
  PENDING = 1,
  /** 审核中 */
  AUDITING = 2,
  /** 审核驳回 */
  REJECT = 3,
  /** 微信审核中 */
  WECHAT_AUDITING = 4,
  /** 微信驳回 */
  WECHAT_REJECT = 5,
  /** 已发布 */
  PUBLISHED = 6,
}

export const appletAuditStatusLabel = {
  [EAppletAuditStatus.NOT_BIND]: '暂未绑定',
  [EAppletAuditStatus.PENDING]: '待发布',
  [EAppletAuditStatus.AUDITING]: '审核中',
  [EAppletAuditStatus.REJECT]: '审核驳回',
  [EAppletAuditStatus.WECHAT_AUDITING]: '微信审核',
  [EAppletAuditStatus.WECHAT_REJECT]: '微信驳回',
  [EAppletAuditStatus.PUBLISHED]: '已发布',
};

export const appletAuditStatusStyle = {
  [EAppletAuditStatus.NOT_BIND]: 'info',
  [EAppletAuditStatus.PENDING]: 'primary',
  [EAppletAuditStatus.AUDITING]: 'primary',
  [EAppletAuditStatus.REJECT]: 'danger',
  [EAppletAuditStatus.WECHAT_AUDITING]: 'primary',
  [EAppletAuditStatus.WECHAT_REJECT]: 'danger',
  [EAppletAuditStatus.PUBLISHED]: 'success',
};

/** 小程序管理配置状态 */
export enum EAppletConfigStatus {
  /** 暂未绑定 */
  NOT_BIND = 0,
  /** 使用中 */
  USING = 1,
}

export const appletConfigStatusLabel = {
  [EAppletConfigStatus.NOT_BIND]: '暂未绑定',
  [EAppletConfigStatus.USING]: '使用中',
};

export enum ETaxSourceType {
  /** 内陆 */
  Mainland = 0,
  /** 海南 */
  Hainan = 1,
}

export const taxSourceTypeLabel = {
  [ETaxSourceType.Mainland]: '内陆',
  [ETaxSourceType.Hainan]: '海南',
};

export const personalInvoiceTaxSourceLocationLabel = {
  [ETaxSourceType.Mainland]: '通用',
  [ETaxSourceType.Hainan]: '海南',
};

/** 税源地类型 */
export enum ETaxSourceItem {
  /** 内陆 */
  MAINLAND = 0,
  /** 海南 */
  HAINAN = 1,
}

export const taxSourceItemLabel = {
  [ETaxSourceItem.MAINLAND]: '内陆',
  [ETaxSourceItem.HAINAN]: '海南',
};

/** 入驻申请状态(0申请中 1审批成功 2审批失败 3已入驻 4入驻失败) */
export enum ETaxSourceSettleStatus {
  /** 申请中 */
  PENDING = 0,
  /** 审批成功 */
  SUCCESS = 1,
  /** 审批失败 */
  REJECT = 2,
  /** 已入驻 */
  SETTLED = 3,
  /** 入驻失败 */
  FAILED = 4,
}

export const taxSourceSettleStatusLabel = {
  [ETaxSourceSettleStatus.PENDING]: '申请中',
  [ETaxSourceSettleStatus.SUCCESS]: '申请中',
  [ETaxSourceSettleStatus.REJECT]: '已驳回',
  [ETaxSourceSettleStatus.SETTLED]: '已入驻',
  [ETaxSourceSettleStatus.FAILED]: '已驳回',
};

export const taxSourceSettleStatusStyle = {
  [ETaxSourceSettleStatus.PENDING]: 'primary',
  [ETaxSourceSettleStatus.SUCCESS]: 'primary',
  [ETaxSourceSettleStatus.REJECT]: 'danger',
  [ETaxSourceSettleStatus.SETTLED]: 'success',
  [ETaxSourceSettleStatus.FAILED]: 'danger',
};

/** 开票额申请状态(0未申请 1审批成功 2审批失败 3已申请 4申请失败) */
export enum ETaxSourceInvoiceStatus {
  /** 申请中 */
  APPLYING = 0,
  /** 审批成功 */
  SUCCESS = 1,
  /** 审批失败 */
  REJECT = 2,
  /** 已申请 */
  APPLIED = 3,
  /** 申请失败 */
  FAILED = 4,
  /** 未申请 */
  NOT_APPLY = 5,
}

export const taxSourceInvoiceStatusLabel = {
  [ETaxSourceInvoiceStatus.APPLYING]: '申请中',
  [ETaxSourceInvoiceStatus.SUCCESS]: '申请中',
  [ETaxSourceInvoiceStatus.REJECT]: '已驳回',
  [ETaxSourceInvoiceStatus.APPLIED]: '已申请',
  [ETaxSourceInvoiceStatus.FAILED]: '已驳回',
  [ETaxSourceInvoiceStatus.NOT_APPLY]: '未申请',
};

export const taxSourceInvoiceStatusStyle = {
  [ETaxSourceInvoiceStatus.APPLYING]: 'primary',
  [ETaxSourceInvoiceStatus.SUCCESS]: 'primary',
  [ETaxSourceInvoiceStatus.REJECT]: 'danger',
  [ETaxSourceInvoiceStatus.APPLIED]: 'success',
  [ETaxSourceInvoiceStatus.FAILED]: 'danger',
  [ETaxSourceInvoiceStatus.NOT_APPLY]: 'info',
};

export interface IBusinessLineItem {
  /** 业务线id */
  businessId: number;
  /** 业务线名称 */
  businessName: string;
  /** 是否默认功能（0：否，1：是） */
  isDefault: 0 | 1;
}

/** 完税状态 */
export enum ETaxInvoiceStatus {
  /** 待支付 */
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  /** 待开票 */
  PENDING_INVOICE = 'PENDING_INVOICE',
  /** 已开票 */
  INVOICED = 'INVOICED',
}

export const taxInvoiceStatusLabel = {
  [ETaxInvoiceStatus.PENDING_PAYMENT]: '待支付',
  [ETaxInvoiceStatus.PENDING_INVOICE]: '待开票',
  [ETaxInvoiceStatus.INVOICED]: '已开票',
};

export enum EHnInvoiceApplyPay {
  /** 微信 */
  WECHAT = 'WECHAT',
  /** 银联云闪付 */
  UNION = 'UNION',
  /** 线上转账 */
  LKLAT = 'LKLAT',
}

export const hnInvoiceApplyPayLabel = {
  [EHnInvoiceApplyPay.WECHAT]: '微信',
  [EHnInvoiceApplyPay.UNION]: '银联云闪付',
  [EHnInvoiceApplyPay.LKLAT]: '线上转账',
};

/** 企业审批状态 */
export enum ECompanyApproveStatus {
  /** 初审中 */
  PENDING = 0,
  /** 复审中 */
  REVIEWING = 1,
  /** 审核驳回 */
  FAILED = 2,
  /** 正在使用 */
  USING = 3,
  /** 审核中 */
  AUDITING = 4,
  /** 编辑初审 */
  EDIT_PENDING = 5,
  /** 编辑复审 */
  EDIT_REVIEWING = 6,
  /** 编辑驳回 */
  EDIT_FAILED = 7,
  /** 编辑审核 */
  EDIT_AUDITING = 8,
}

export const companyApproveStatusLabel = {
  [ECompanyApproveStatus.PENDING]: '初审中',
  [ECompanyApproveStatus.REVIEWING]: '复审中',
  [ECompanyApproveStatus.FAILED]: '审核驳回',
  [ECompanyApproveStatus.USING]: '正在使用',
  [ECompanyApproveStatus.AUDITING]: '审核中',
  [ECompanyApproveStatus.EDIT_PENDING]: '编辑初审',
  [ECompanyApproveStatus.EDIT_REVIEWING]: '编辑复审',
  [ECompanyApproveStatus.EDIT_FAILED]: '编辑驳回',
  [ECompanyApproveStatus.EDIT_AUDITING]: '编辑审核',
};

/** 完税发票申请状态 */
export enum ETaxInvoiceApplyStatus {
  /** 含税发票 */
  STANDARD = 'STANDARD',
  /** 免税发票 */
  FREE_TAX = 'FREE_TAX',
}

export const taxInvoiceApplyStatusLabel = {
  [ETaxInvoiceApplyStatus.STANDARD]: '含税发票',
  [ETaxInvoiceApplyStatus.FREE_TAX]: '免税发票',
};

export interface ISecuritySettingDetail {
  /** 支付密码 */
  payPhone: string;
  /** 是否设置支付密码 0:未设置 1:已设置 */
  hasPayPassword: EHasPayPassword;
  /** 是开通免短信验证     smsVerify = true 未开通免短信验证 */
  smsVerify: boolean;
}

export enum EHasPayPassword {
  /** 未设置 */
  NOT_SET = 0,
  /** 已设置 */
  SET = 1,
}

export enum EBlackListType {
  /** 身份证件号 */
  ID_NO = 'ID_NO',
  /** 手机号 */
  PHONE = 'PHONE',
  /** 统一社会信用代码 */
  CREDIT_CODE = 'CREDIT_CODE',
}

export const blackListTypeLabel = {
  [EBlackListType.ID_NO]: '身份证件号',
  [EBlackListType.PHONE]: '手机号',
  [EBlackListType.CREDIT_CODE]: '统一社会信用代码',
};

export enum EBlackListCheckResult {
  /** 通过 */
  SUCCESS = 'SUCCESS',
  /** 字段类型错误 */
  FAIL_TYPE = 'FAIL_TYPE',
  /** 字段格式错误 */
  FAIL_FORMAT = 'FAIL_FORMAT',
  /** 字段内容重复 */
  FAIL_REPEAT = 'FAIL_REPEAT',
}

export const blackListCheckResultLabel = {
  [EBlackListCheckResult.SUCCESS]: '通过',
  [EBlackListCheckResult.FAIL_TYPE]: '字段类型错误',
  [EBlackListCheckResult.FAIL_FORMAT]: '字段格式错误',
  [EBlackListCheckResult.FAIL_REPEAT]: '字段内容重复',
};

export enum EBlackListExportTemplate {
  /** 发放限制模板  */
  GrantLimit = 'https://lg5-prod-1314932667.cos.ap-nanjing.myqcloud.com/web-example/%E5%8F%91%E6%94%BE%E9%99%90%E5%88%B6%E9%BB%91%E5%90%8D%E5%8D%95%E6%A8%A1%E7%89%88.xlsx',
  /** 登录限制模板 */
  LoginLimit = 'https://lg5-prod-1314932667.cos.ap-nanjing.myqcloud.com/web-example/%E7%99%BB%E5%BD%95%E9%99%90%E5%88%B6%E9%BB%91%E5%90%8D%E5%8D%95%E6%A8%A1%E7%89%88.xlsx'
}

export enum EWhiteListTerm {
  /** 长期 */
  LONG_TERM = 'LONG_TERM',
  /** 生效中 */
  EFFECTIVE = 'EFFECTIVE',
  /** 已失效 */
  EXPIRED = 'EXPIRED',
}

export const whiteListTermLabel = {
  [EWhiteListTerm.LONG_TERM]: '长期',
  [EWhiteListTerm.EFFECTIVE]: '生效中',
  [EWhiteListTerm.EXPIRED]: '已失效',
};

/**
 * 业务类型
 */
export enum EBusinessTags {
  /** 基础管理 */
  BASE = 1,
  /** 智慧分账 */
  AIR_SPLITTING = 2,
  /** 众包 */
  CROWD_SOURCE = 3,
  /** 撮合 */
  BROKER_LABOR = 4
}

export const businessTagsConfig = {
  [EBusinessTags.BASE]: '基础管理',
  [EBusinessTags.AIR_SPLITTING]: '智慧分账',
  [EBusinessTags.CROWD_SOURCE]: '众包灵工',
  [EBusinessTags.BROKER_LABOR]: '撮合灵工',
};

/**
 * 基础管理常用入口
 */
export enum EBaseCommonEntrance {
  /** 我的钱包 */
  BASE_WALLET = 'BASE_WALLET',
  /** 合同管理 */
  BASE_CONTRACT = 'BASE_CONTRACT',
  /** 自由职业者 */
  BASE_CUSTOMER = 'BASE_CUSTOMER',
}

export const baseCommonEntranceLabel = {
  [EBaseCommonEntrance.BASE_WALLET]: '我的钱包',
  [EBaseCommonEntrance.BASE_CONTRACT]: '合同管理',
  [EBaseCommonEntrance.BASE_CUSTOMER]: '自由职业者',
};

/**
 * 收入性质
 */
export enum EProxyPayType {
  /** 经营所得 */
  BUSINESS_INCOME = 'BUSINESS_INCOME',
  /** 劳务报酬 */
  LABOR_REMUNERATION = 'LABOR_REMUNERATION',
}

export const proxyPayTypeConfig = {
  [EProxyPayType.BUSINESS_INCOME]: '经营所得',
  [EProxyPayType.LABOR_REMUNERATION]: '劳务报酬',
};

/**
 * 开票方式
 */
export enum EInvoiceTaxMethod {
  /** 全额征税 - 按交易全部价款计算税额并开具发票 */
  FULL_AMOUNT_TAX = 'FULL_AMOUNT_TAX',
  /** 差额征税 - 按差额（全部价款-扣除项目）计算税额并开具发票 */
  DIFFERENTIAL_TAX = 'DIFFERENTIAL_TAX'
}

export const invoiceTaxMethodConfig = {
  [EInvoiceTaxMethod.FULL_AMOUNT_TAX]: '全额征税',
  [EInvoiceTaxMethod.DIFFERENTIAL_TAX]: '差额征税',
};

/**
 * 子订单类型
 * 系统创建，接口创建，线下导入，接口导入
 */
export enum ESubOrderType {
  /** 系统创建 */
  SYSTEM_CREATE = 'SYSTEM_CREATE',
  /** 接口创建 */
  INTERFACE_CREATE = 'INTERFACE_CREATE',
  /** 线下导入 */
  OFFLINE_IMPORT = 'OFFLINE_IMPORT',
  /** 接口导入 */
  INTERFACE_IMPORT = 'INTERFACE_IMPORT',
}

export const subOrderTypeLabel = {
  [ESubOrderType.SYSTEM_CREATE]: '系统创建',
  [ESubOrderType.INTERFACE_CREATE]: '接口创建',
  [ESubOrderType.OFFLINE_IMPORT]: '线下导入',
  [ESubOrderType.INTERFACE_IMPORT]: '接口导入',
};

/**
 * 产品类型
 */
export enum EProductProxyType {
  /** 众包灵工-经营所得 */
  CROWD_BUSINESS_INCOME = 'CROWD_BUSINESS_INCOME',
  /** 众包灵工-劳务报酬 */
  CROWD_LABOR_REMUNERATION = 'CROWD_LABOR_REMUNERATION',
  /** 撮合灵工-经营所得 */
  MATCH_BUSINESS_INCOME = 'MATCH_BUSINESS_INCOME',
}

export const productProxyTypeLabel = {
  [EProductProxyType.CROWD_BUSINESS_INCOME]: '众包灵工-经营所得',
  [EProductProxyType.CROWD_LABOR_REMUNERATION]: '众包灵工-劳务报酬',
  [EProductProxyType.MATCH_BUSINESS_INCOME]: '撮合灵工-经营所得',
};

/**
 * 风险等级
 */
export enum ERiskLevel {
  /** 一级 */
  LEVEL_1 = 0,
  /** 二级 */
  LEVEL_2 = 1,
  /** 三级 */
  LEVEL_3 = 2,
  /** 四级 */
  LEVEL_4 = 3,
  /** 五级 */
  LEVEL_5 = 4,
}

export const riskLevelLabel = {
  [ERiskLevel.LEVEL_1]: '一级',
  [ERiskLevel.LEVEL_2]: '二级',
  [ERiskLevel.LEVEL_3]: '三级',
  [ERiskLevel.LEVEL_4]: '四级',
  [ERiskLevel.LEVEL_5]: '五级',
};

/** 角色部门权限 */
export enum ERoleDepartmentPermission {
  /** 自己 */
  SELF = 'SELF',
  /** 所属部门 */
  DEPT = 'DEPT',
  /** 所属部门及子部门 */
  DEPT_WITH_CHILDREN = 'DEPT_WITH_CHILDREN',
  /** 全部 */
  ALL = 'ALL',
  /** 自定义权限 */
  CUSTOM = 'CUSTOM',
}

export const roleDepartmentPermissionLabel = {
  [ERoleDepartmentPermission.SELF]: '自己',
  [ERoleDepartmentPermission.DEPT]: '所属部门',
  [ERoleDepartmentPermission.DEPT_WITH_CHILDREN]: '所属部门及子部门',
  [ERoleDepartmentPermission.ALL]: '全部',
  [ERoleDepartmentPermission.CUSTOM]: '自定义权限',
};

/** 服务商账户状态 */
export enum EServerType{
  /** 自有服务商 */
  SELF_SERVER = 'SELF_SERVER',
  /** 三方服务商 */
  THIRD_PARTY_SERVER = 'THIRD_PARTY_SERVER',
}

export const serverTypeLabel = {
  [EServerType.SELF_SERVER]: '自有服务商',
  [EServerType.THIRD_PARTY_SERVER]: '三方服务商',
};

/** 海南开票方式 */
export enum EHnInvoiceType {
  /** 线上开票 */
  ONLINE = 'ONLINE',
  /** 线下开票 */
  OFFLINE = 'OFFLINE',
}

export const hnInvoiceTypeLabel = {
  [EHnInvoiceType.ONLINE]: '线上开票',
  [EHnInvoiceType.OFFLINE]: '线下开票',
};

/** 海南线下-完税授权方式 */
export enum EHnOfflineAuthMethod {
  /** 三方 */
  THREE_PARTY = 1,
  /** 纯线下 */
  OFFLINE = 0,
}

export const hnOfflineAuthMethodLabel = {
  [EHnOfflineAuthMethod.OFFLINE]: '纯线下',
  [EHnOfflineAuthMethod.THREE_PARTY]: '第三方',
};

/** 人脸认证结果 */
export enum EFaceAuth {
  /** 已验证 */
  VERIFIED = 1,
  /** 未验证 */
  UNVERIFIED = 0,
}

export const faceAuthLabel = {
  [EFaceAuth.VERIFIED]: '已验证',
  [EFaceAuth.UNVERIFIED]: '未验证',
};

export const faceAuthStyle = {
  [EFaceAuth.VERIFIED]: 'success',
  [EFaceAuth.UNVERIFIED]: 'info',
};

export const serverCrowdCreditCode = ['91320117MA277NMR9D', '91341300MAE1XUXB5K'];

export const ahFreePartnerCreditCode = ['91341300MAE1XUXB5K'];